import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import { Topbar } from '@/components/Topbar';
import { EmptyState } from '@/components/EmptyState';
import type { CampaignLogEntry } from '@/types/domain';

type LogType = 'all' | 'join' | 'seed';
type LogScope = 'upcoming' | 'completed';

export function CampaignLogsPage(): JSX.Element {
  const { id } = useParams();
  const cid = Number(id);
  const [type, setType] = useState<LogType>('all');
  const [scope, setScope] = useState<LogScope>('upcoming');

  const { data, loading, error } = useAsync(
    () => api.listCampaignLogs(cid, scope, type),
    [cid, scope, type],
  );

  if (loading && !data) {
    return (
      <div className="space-y-3">
        <Topbar title="Логи задач" subtitle="по чатам и статусам" />
        <div className="skeleton h-12" />
        <div className="skeleton h-24" />
        <div className="skeleton h-24" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-8">
      <Topbar
        title="Логи задач"
        subtitle={`${data?.items.length ?? 0} элементов · отдельно по каждому чату`}
      />

      <div className="flex gap-2 overflow-x-auto pb-1">
        <FilterButton active={scope === 'upcoming'} onClick={() => setScope('upcoming')}>
          Предстоящие
        </FilterButton>
        <FilterButton active={scope === 'completed'} onClick={() => setScope('completed')}>
          Выполненные
        </FilterButton>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        <FilterButton active={type === 'all'} onClick={() => setType('all')}>
          Все
        </FilterButton>
        <FilterButton active={type === 'join'} onClick={() => setType('join')}>
          Вступление
        </FilterButton>
        <FilterButton active={type === 'seed'} onClick={() => setType('seed')}>
          Посев
        </FilterButton>
      </div>

      {!data?.items.length ? (
        <EmptyState
          icon="search"
          title="Логи пусты"
          body={error || 'Под выбранные фильтры задач для этой кампании пока нет.'}
        />
      ) : (
        <ul className="space-y-3">
          {data.items.map((item) => (
            <li key={item.id} className="card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-[15px] font-semibold text-white">{item.targetTitle}</div>
                  <div className="mt-1 truncate text-[13px] text-[#7e87aa]">{item.normalizedLink}</div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span
                    className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ${
                      item.type === 'join'
                        ? 'border-[#435bff] bg-[#121c49] text-[#cad4ff]'
                        : 'border-[#285b45] bg-[rgba(33,91,61,0.22)] text-[#59dc97]'
                    }`}
                  >
                    {item.type === 'join' ? 'join' : 'seed'}
                  </span>
                  <span
                    className={`rounded-full px-3 py-1 text-[11px] font-semibold ${statusClass(item.status)}`}
                  >
                    {statusLabel(item.status)}
                  </span>
                </div>
              </div>

              <div className="mt-4 grid gap-2 text-[13px] text-[#d9def5]">
                <LogRow label="Статус" value={statusLabel(item.status)} />
                <LogRow label="Запланировано" value={formatDateTime(item.scheduledAt)} />
                <LogRow label="Старт" value={item.startedAt ? formatDateTime(item.startedAt) : 'ещё нет'} />
                <LogRow label="Финиш" value={item.finishedAt ? formatDateTime(item.finishedAt) : 'ещё нет'} />
                <LogRow
                  label={item.type === 'join' ? 'Покрытие' : 'Сообщений'}
                  value={
                    item.type === 'join'
                      ? `${item.joinedCount ?? 0}/2 аккаунта`
                      : String(item.messagesPosted ?? 0)
                  }
                />
                <LogRow label="Ошибка" value={item.lastError || 'нет'} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.12em] transition ${
        active
          ? 'border-[#435bff] bg-[#121c49] text-[#c9d3ff]'
          : 'border-white/10 bg-white/4 text-[#848cab]'
      }`}
    >
      {children}
    </button>
  );
}

function LogRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3 rounded-[18px] border border-white/6 bg-white/4 px-4 py-3">
      <div className="text-[#8b94b7]">{label}</div>
      <div className="max-w-[60%] text-right text-white">{value}</div>
    </div>
  );
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

function statusLabel(status: CampaignLogEntry['status']) {
  switch (status) {
    case 'scheduled':
      return 'в очереди';
    case 'running':
      return 'идёт';
    case 'posted':
      return 'завершён';
    case 'failed':
      return 'ошибка';
    case 'cancelled':
      return 'отменён';
    case 'joined':
      return 'вступили';
    case 'alreadyJoined':
      return 'уже были';
    case 'pending':
      return 'ожидает';
    case 'requested':
      return 'запрошен доступ';
    case 'skipped':
      return 'пропущен';
    default:
      return status;
  }
}

function statusClass(status: CampaignLogEntry['status']) {
  switch (status) {
    case 'posted':
    case 'joined':
    case 'alreadyJoined':
      return 'bg-emerald-500/15 text-emerald-200';
    case 'running':
    case 'pending':
    case 'requested':
    case 'scheduled':
      return 'bg-sky-500/15 text-sky-200';
    case 'failed':
    case 'cancelled':
    case 'skipped':
      return 'bg-rose-500/15 text-rose-200';
    default:
      return 'bg-white/8 text-[#c9d3ff]';
  }
}
